import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@dg/ui";
import { validateInvite, signIn, isApiError } from "../lib/api.js";
import { AuthSpinner } from "../components/layout/auth-spinner.js";
import { useAuth } from "../contexts/auth.js";

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  );
}

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026c.462-.62.874-1.275 1.226-1.963.021-.04.001-.088-.041-.104a13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

type State = "loading" | "valid" | "invalid" | "already-logged-in";

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { auth } = useAuth();
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    if (auth.status === "loading") return;

    // Already authenticated — they don't need to sign in via invite
    if (auth.status === "active") {
      setState("already-logged-in");
      return;
    }

    if (!token) {
      setState("invalid");
      return;
    }

    validateInvite(token)
      .then(() => setState("valid"))
      .catch((err) => {
        if (isApiError(err) && err.status === 400) {
          setState("invalid");
        } else {
          setState("invalid");
        }
      });
  }, [token, auth.status]);

  if (auth.status === "loading" || state === "loading") return <AuthSpinner />;

  if (state === "already-logged-in") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="text-4xl font-bold text-primary">◆</div>
        <h1 className="text-2xl font-bold">You're already signed in</h1>
        <p className="text-muted-foreground max-w-sm">
          You are already logged in. Visit the dashboard to access your apps.
        </p>
        <a
          href="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 min-h-[44px]"
        >
          Go to dashboard
        </a>
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="text-4xl font-bold text-destructive">✕</div>
        <h1 className="text-2xl font-bold">Invite link invalid or expired</h1>
        {/* Generic message per SCOPE.md §12 — never distinguish between not-found/expired/used */}
        <p className="text-muted-foreground max-w-sm">
          This invite link is invalid or has expired. Contact{" "}
          <a
            href="mailto:admin@jensen-dowell.me"
            className="underline hover:text-foreground"
          >
            admin@jensen-dowell.me
          </a>{" "}
          to request a new one.
        </p>
      </div>
    );
  }

  // Valid invite — show sign-in options
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-8">
      <div className="text-center space-y-2">
        <div className="text-5xl font-bold text-primary">◆</div>
        <h1 className="text-3xl font-bold tracking-tight">
          You've been invited
        </h1>
        <p className="text-muted-foreground max-w-sm">
          Sign in with your account to accept your invitation and activate your
          access.
        </p>
      </div>

      {/* Privacy collection notice — APP 1/5 (SCOPE.md §12) */}
      <p className="text-xs text-muted-foreground max-w-xs text-center">
        By signing in you consent to collection of your OAuth profile (name,
        email, avatar) and usage logs for platform operation. See{" "}
        <a href="/privacy" className="underline hover:text-foreground">
          Privacy Notice
        </a>
        .
      </p>

      <div className="flex flex-col gap-3 w-full max-w-[240px]">
        <Button
          className="gap-2 min-h-[44px]"
          onClick={() => void signIn("github")}
        >
          <GitHubIcon className="h-5 w-5" />
          Sign in with GitHub
        </Button>
        <Button
          variant="outline"
          className="gap-2 min-h-[44px]"
          onClick={() => void signIn("discord")}
        >
          <DiscordIcon className="h-5 w-5" />
          Sign in with Discord
        </Button>
        <Button
          variant="outline"
          className="gap-2 min-h-[44px]"
          onClick={() => void signIn("linkedin")}
        >
          <LinkedInIcon className="h-5 w-5" />
          Sign in with LinkedIn
        </Button>
      </div>
    </div>
  );
}
